import { type BrandTokens, estimateTextWidth, type TextMeasure } from "@cva/shared";
import { useEffect, useState } from "react";

/**
 * Exact text widths with the real fonts (canvas), for layouts built in the
 * browser such as library templates. Fonts must be loaded first
 * (`useFontsReady`), otherwise the canvas measures a fallback font.
 */

let context: CanvasRenderingContext2D | null | undefined;

export const canvasMeasure: TextMeasure = (text, style) => {
  if (context === undefined) context = document.createElement("canvas").getContext("2d");
  if (!context) return estimateTextWidth(text, style);
  const value = style.uppercase ? text.toUpperCase() : text;
  const spacing = style.letterSpacing * style.fontSize;
  context.font = `${style.italic ? "italic " : ""}${style.fontWeight} ${style.fontSize}px "${style.fontFamily}"`;
  // Older browsers without canvas letter spacing: add it by hand.
  const canSpace = "letterSpacing" in (context as object);
  if (canSpace) context.letterSpacing = `${spacing}px`;
  return context.measureText(value).width + (canSpace ? 0 : spacing * value.length);
};

interface Face {
  family: string;
  weight: number;
  italic: boolean;
}

const loads = new Map<string, Promise<unknown>>();
const faceKey = (face: Face) =>
  `${face.italic ? "italic" : "normal"} ${face.weight} "${face.family}"`;

function loadFace(face: Face): Promise<unknown> {
  const key = faceKey(face);
  let load = loads.get(key);
  if (!load) {
    load = document.fonts.load(`${key.replace(/ "/, ' 64px "')}`, "AaÄäÖöÜüß0123").catch(() => []);
    loads.set(key, load);
  }
  return load;
}

/** Font faces of a kit's text styles. */
export function tokenFaces(tokens: BrandTokens): Face[] {
  return tokens.textStyles.map((style) => ({
    family: style.fontFamily,
    weight: style.fontWeight,
    italic: style.italic,
  }));
}

/**
 * True once all faces are loaded. `enabled` waits for something else first
 * (e.g. the organization's own fonts being registered).
 */
export function useFontsReady(faces: Face[], enabled = true): boolean {
  const key = [...new Set(faces.map(faceKey))].sort().join("|");
  const [readyKey, setReadyKey] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled || typeof document === "undefined" || !("fonts" in document)) return;
    let cancelled = false;
    const list = key ? key.split("|") : [];
    const timeout = new Promise((resolve) => setTimeout(resolve, 4000));
    void Promise.race([
      Promise.all(
        list.map((entry) => {
          const match = /^(\w+) (\d+) "(.*)"$/.exec(entry);
          return match
            ? loadFace({
                italic: match[1] === "italic",
                weight: Number(match[2]),
                family: match[3]!,
              })
            : Promise.resolve();
        }),
      ),
      timeout,
    ]).then(() => {
      if (!cancelled) setReadyKey(key);
    });
    return () => {
      cancelled = true;
    };
  }, [key, enabled]);
  return enabled && readyKey === key;
}
